<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                :pages="[
                    {
                        name: $tc('messages.programmings', 2),
                        href: meta.links.index,
                    },
                    {
                        name: programming.title,
                        href: programming.links.show,
                    },
                    {
                        name:
                            $tc('messages.sessions', 1) +
                            ' ' +
                            session.order +
                            ' ' +
                            session.programming_subject.subject.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="flex justify-between">
            <div>
                <AppButton v-if="meta.links.previous" :href="meta.links.previous" element="inertia-link">
                    {{
                        $tc('messages.sessions', 1) +
                        ' ' +
                        (session.order - 1) +
                        ' ' +
                        session.programming_subject.subject.name
                    }}
                </AppButton>
            </div>
            <div>
                <AppButton v-if="meta.links.next" :href="meta.links.next" element="inertia-link">
                    {{
                        $tc('messages.sessions', 1) +
                        ' ' +
                        (session.order + 1) +
                        ' ' +
                        session.programming_subject.subject.name
                    }}
                </AppButton>
            </div>
        </div>

        <div class="mt-5">
            <h2 class="text-lg leading-6 font-medium text-gray-900 mb-3">
                <span>{{ $tc('messages.activities', 2) }}</span>

                <AddSessionActivitiesForm
                    :programming="programming"
                    :programming_subject="session.programming_subject"
                    :session="session"
                >
                    <svg
                        class="w-6 h-6 text-gray-700"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                </AddSessionActivitiesForm>
            </h2>

            <div>
                {{ session.programming_subject.subject.name }}
            </div>
            <div v-for="group in session.groups" :key="group.id" class="mt-3">
                <div v-if="group.date_display">
                    <div class="flex space-x-1 items-center">
                        <AppButton element="a" :href="group.url">
                            {{ group.name }}
                        </AppButton>
                        <span>
                            {{ group.date_display }}
                        </span>
                    </div>
                </div>
            </div>

            <AddSessionGroupsForm :session="session" :groups="programming.groups" class="mt-3">
                <svg
                    class="w-6 h-6"
                    :class="session.groups_count ? 'text-indigo-700' : 'text-gray-700'"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </AddSessionGroupsForm>

            <div class="flex flex-col mt-5">
                <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                    <div
                        class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                    >
                        <table class="table-fixed min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr>
                                    <th
                                        class="px-6 py-3 bg-blue-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.activities', 2) }}
                                    </th>
                                    <th
                                        class="w-1/4 max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                    >
                                        {{ $tc('messages.materials', 2) }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    :class="activityIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                    v-for="(activity, activityIndex) in session.activities"
                                    :key="activity.id"
                                >
                                    <td class="truncate px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-900">
                                        <div class="flex space-x-2">
                                            <div class="flex flex-col space-y-1">
                                                <a
                                                    :href="route('activities.show', activity.id)"
                                                    class="inline-flex items-center px-1 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <svg viewBox="0 0 20 20" fill="currentColor" class="eye w-3 h-3">
                                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                                        <path
                                                            fill-rule="evenodd"
                                                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                                            clip-rule="evenodd"
                                                        ></path>
                                                    </svg>
                                                </a>
                                                <a
                                                    :href="route('activities.edit', activity.id)"
                                                    class="inline-flex items-center px-1 py-1 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    <svg viewBox="0 0 20 20" fill="currentColor" class="eye w-3 h-3">
                                                        <path
                                                            d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"
                                                        ></path>
                                                    </svg>
                                                </a>
                                            </div>
                                            <div>
                                                <div class="flex space-x-2">
                                                    <div
                                                        class="inline-block"
                                                        :title="$t('messages.to_be_graded')"
                                                        v-if="activity.weight"
                                                    >
                                                        <svg
                                                            class="w-4 h-4 text-indigo-400"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fill-rule="evenodd"
                                                                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                                clip-rule="evenodd"
                                                            ></path>
                                                        </svg>
                                                    </div>
                                                    <span class="font-semibold">{{ activity.title }}</span>
                                                    <span v-if="activity.grouping_display"
                                                        >({{ activity.grouping_display }})</span
                                                    >
                                                    <span v-if="activity.duration"
                                                        >- {{ activity.duration
                                                        }}{{ $t('messages.minute_abbreviation') }}</span
                                                    >
                                                    <span
                                                        v-if="activity.attention_to_diversity !== null"
                                                        :title="$t('messages.attention_to_diversity')"
                                                    >
                                                        <svg
                                                            class="inline-block w-4 h-4 text-indigo-600"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                fill-rule="evenodd"
                                                                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-2 0c0 .993-.241 1.929-.668 2.754l-1.524-1.525a3.997 3.997 0 00.078-2.183l1.562-1.562C15.802 8.249 16 9.1 16 10zm-5.165 3.913l1.58 1.58A5.98 5.98 0 0110 16a5.976 5.976 0 01-2.516-.552l1.562-1.562a4.006 4.006 0 001.789.027zm-4.677-2.796a4.002 4.002 0 01-.041-2.08l-.08.08-1.53-1.533A5.98 5.98 0 004 10c0 .954.223 1.856.619 2.657l1.54-1.54zm1.088-6.45A5.974 5.974 0 0110 4c.954 0 1.856.223 2.657.619l-1.54 1.54a4.002 4.002 0 00-2.346.033L7.246 4.668zM12 10a2 2 0 11-4 0 2 2 0 014 0z"
                                                                clip-rule="evenodd"
                                                            ></path>
                                                        </svg>
                                                    </span>
                                                </div>
                                                <p class="max-w-2xl break-words whitespace-normal">
                                                    {{ activity.description }}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="truncate px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-900">
                                        <div v-for="material in activity.materials" :key="material.id">
                                            <span v-if="material.url === null">
                                                {{ material.title }}
                                            </span>
                                            <a
                                                v-else
                                                :href="material.url"
                                                v-text="material.title"
                                                class="text-indigo-500"
                                            ></a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AddSessionActivitiesForm from '@/components/pageSpecific/programming/AddSessionActivitiesForm.vue';
import AddSessionGroupsForm from '@/components/pageSpecific/programming/AddSessionGroupsForm.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    components: {
        AppButton,
        AddSessionActivitiesForm,
        PanelPage,
        AddSessionGroupsForm,
    },
    props: ['meta', 'programming', 'session'],
    layout: Layout,
    data() {
        return {
            showAddSessions: null,
        };
    },
    name: 'Show',
    computed: {},
    methods: {},
};
</script>
